import { CreateReferringPhysicianDrawer } from '@/features/create-referring-physician';
import { ConfirmModal, CustomButton, CustomSelect } from '@/shared/ui/components';
import { getContent } from '@/entities/study';
import { addNewIcon, inforBlackIcon } from '@/shared/ui/assets';
import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { Drawer } from 'antd';
import dayjs from 'dayjs';
import dayjsBusinessDays from 'dayjs-business-days';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { t } from 'ttag';
import { useEditRefPhysicianDrawer } from './useEditRefPhysicianDrawer';
import type { EditReferringPhysicianProps } from '../editStudyDetails.types';

dayjs.extend(dayjsBusinessDays);
dayjs.extend(utc);

const EditRefPhysicianDrawer = ({
  data,
  open,
  referringPhysicians,
  countries,
  handleUpdateReferringPhysicians,
  onClose,
  drawerKey,
  updateStudyData,
  ...params
}: EditReferringPhysicianProps) => {
  const {
    state,
    setState,
    isAbortedStudy,
    handleReferringPhysicianChange,
    handleClickSave,
    handleOnClose,
    onSave,
    handleClickCreateReferringPhysician,
    toggleDisplayCreateReferringPhysicianDrawer,
  } = useEditRefPhysicianDrawer({
    data,
    open,
    referringPhysicians,
    handleUpdateReferringPhysicians,
    onClose,
    drawerKey,
    updateStudyData,
  });

  return (
    <Drawer
      {...params}
      open={open}
      rootClassName="edit-study-drawer-wrapper"
      title={t`Referring physician`}
      aria-label={t`Referring physician`}
      onClose={handleOnClose}
      destroyOnClose
      maskClosable={false}
    >
      {state.isInitiating ? (
        <div className="edit-study-details">
          <Skeleton className="initiating-skeleton" />
        </div>
      ) : (
        <>
          <CreateReferringPhysicianDrawer
            visible={state.visibleCreateReferringPhysicianDrawer}
            countries={countries || []}
            data={data as any}
            handleClickClose={toggleDisplayCreateReferringPhysicianDrawer}
            handleClickCreate={handleClickCreateReferringPhysician}
            isCreatingReferringPhysician={state.isCreatingReferringPhysician}
          />
          <ConfirmModal
            title={t`Apply changes`}
            question={getContent()}
            isOpen={state.isOpenConfirmModal}
            leftButtonName={t`Cancel`}
            rightButtonName={t`Apply changes`}
            onClickLeftButton={() => {
              setState({ isOpenConfirmModal: false });
            }}
            onClickRightButton={() => {
              onSave();
            }}
            isSaving={state.isSaving}
          />

          <div className="edit-study-details">
            <div className="content-container">
              <div className="content">
                <div className="left">
                  <div className="title">{t`Referring physician`}</div>
                </div>
                <div className="right">
                  <div>
                    <CustomSelect
                      className="referring-physician-list-select"
                      name="referringPhysician"
                      value={state.selectedReferringPhysician}
                      size="middle"
                      onChange={handleReferringPhysicianChange}
                      placeholder={t`Select referring physician`}
                      allowClear={false}
                      aria-label={t`Referring physician`}
                      options={
                        [
                          {
                            label: (
                              <div
                                className="add-new-physician"
                                aria-hidden
                                onClick={toggleDisplayCreateReferringPhysicianDrawer}
                              >
                                {t`Add new physician`}
                                <img src={addNewIcon} alt="addNewIcon" />
                              </div>
                            ),
                            options: referringPhysicians,
                          },
                        ] as any
                      }
                      disabled={isAbortedStudy}
                      labelInValue
                      id="select-referring-physician"
                    />
                    {isAbortedStudy && (
                      <div className="abort-input">
                        <img src={inforBlackIcon} alt="inforBlackIcon" />
                        {t`Cannot edit referring physician of aborted study.`}
                      </div>
                    )}
                  </div>
                  <div className="referring-info">
                    <div className="name">{state.selectedReferringPhysician?.name}</div>
                    <div className="infor">
                      <div className="label">{`${t`Day phone`}:`}</div>
                      <div className="value">
                        {!_.isEmpty(state.selectedReferringPhysician)
                          ? state.selectedReferringPhysician.phone1 || DOUBLE_HYPHEN_BULLET
                          : data?.info?.referringPhysician?.contact?.phone1 || DOUBLE_HYPHEN_BULLET}
                      </div>
                      <div className="label">{`${t`After hours phone`}:`}</div>
                      <div className="value">
                        {!_.isEmpty(state.selectedReferringPhysician)
                          ? state.selectedReferringPhysician.nightPhone || DOUBLE_HYPHEN_BULLET
                          : data?.info?.referringPhysician?.contact?.nightPhone ||
                            DOUBLE_HYPHEN_BULLET}
                      </div>
                      <div className="label">{`${t`Email`}:`}</div>
                      <div className="value">
                        {!_.isEmpty(state.selectedReferringPhysician)
                          ? state.selectedReferringPhysician.email || DOUBLE_HYPHEN_BULLET
                          : data?.info?.referringPhysician?.email || DOUBLE_HYPHEN_BULLET}
                      </div>
                      <div className="label">{`${t`Fax number`}:`}</div>
                      <div className="value">
                        {!_.isEmpty(state.selectedReferringPhysician)
                          ? state.selectedReferringPhysician.fax || DOUBLE_HYPHEN_BULLET
                          : data?.info?.referringPhysician?.contact?.fax || DOUBLE_HYPHEN_BULLET}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="edit-study-button-container">
                <CustomButton
                  id="cancel-btn"
                  type="outline"
                  style="neutral"
                  size="middle"
                  onClick={handleOnClose}
                >
                  {t`Cancel`}
                </CustomButton>
                <CustomButton
                  id="save-btn"
                  type="primary"
                  style="default"
                  size="middle"
                  onClick={handleClickSave}
                >
                  {t`Save`}
                </CustomButton>
              </div>
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
};

export default EditRefPhysicianDrawer;
